import * as PopoverPrimitive from '@radix-ui/react-popover';
import { ComponentPropsWithoutRef, ReactNode } from 'react';

import { cn } from 'styles';
import { OverlayCommonProps } from './types';
import { OverlayHeader } from './Header';
import { Column } from '../Containers';
import { XIcon } from '../Icons';
import { OVERLAY_BG_IDS } from './escape';

const PopoverRoot = PopoverPrimitive.Root;

const PopoverTrigger = PopoverPrimitive.Trigger;

const PopoverArrow = PopoverPrimitive.Arrow;

const PopoverClose = PopoverPrimitive.Close;

type PrimitiveContentProps = ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>;

/**
 * Pass this into a wrapper that has mouse/touch
 * event handling to prevent the popover to auto-close
 *
 * TODO: Abstract to a minimal forwardRef comp
 */
export const popOverAwarenessProps = {
  'data-radix-popper-content-wrapper': true,
};

type PopoverProps = Pick<
  PrimitiveContentProps,
  'align' | 'sideOffset' | 'className' | 'side' | 'onInteractOutside'
> &
  OverlayCommonProps & {
    children: ReactNode;

    id?: string;

    /**
     * If you need to ensure the trigger gets rendered even if
     * state is being controlled via the open/close props
     */
    byPassTrigger?: ReactNode;

    /**
     * Ensures a safe distance from collision boundaries
     *
     * Default is 20px on all 4 directions
     * Passing any object here completely overwrites the default
     */
    collisionPadding?: PrimitiveContentProps['collisionPadding'];

    /**
     * Adds an arrow to the center of the trigger
     */
    arrow?: boolean;
    arrowClassName?: string;

    /**
     * Sets the container width to scale with its content
     *
     * You can fine tune this via classes, but this is a quick replicable way
     */
    autoWidth?: boolean;
  };

const defaultCollisionPadding = {
  top: 20,
  bottom: 20,
  left: 20,
  right: 20,
};

export function Popover({
  align = 'center',
  collisionPadding = defaultCollisionPadding,
  sideOffset = 4,
  className,
  children,
  isOpen,
  close,
  arrow,
  arrowClassName,
  title,
  description,
  gap,
  trigger,
  showCloseButton,
  byPassTrigger,
  preventOpenAutoFocus,
  autoWidth,
  id,
  ...props
}: PopoverProps) {
  return (
    <PopoverRoot open={isOpen} onOpenChange={(open) => !open && close?.()}>
      <PopoverTrigger asChild>{byPassTrigger ?? trigger}</PopoverTrigger>

      {/* Fake bg element to target on escape call */}
      <div
        className="fixed -left-[2000px] -top-[2000px] h-1 w-1"
        id={id ?? OVERLAY_BG_IDS.popOver}
      />

      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align={align}
          sideOffset={sideOffset}
          onCloseAutoFocus={(e) => e.preventDefault()}
          onOpenAutoFocus={preventOpenAutoFocus ? (e) => e.preventDefault() : undefined}
          collisionPadding={collisionPadding}
          {...props}
          className={cn(
            !autoWidth && 'w-60',
            'relative z-50 rounded-md border bg-popover p-3 text-foreground shadow-md outline-none',
            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
            className,
          )}
        >
          <Column gap={gap}>
            <OverlayHeader
              title={title}
              description={description}
              isDialog={false}
              sizing="reduced"
            />

            {children}
          </Column>

          {arrow && <PopoverArrow className={cn('fill-border/70', arrowClassName)} />}

          {showCloseButton && (
            <PopoverClose
              onClick={close}
              className="absolute right-3 top-3 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary"
            >
              <XIcon size={14} />
              <span className="sr-only">Close</span>
            </PopoverClose>
          )}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverRoot>
  );
}
